import { useCallback, useEffect, useState } from "react";
import { PageContainer } from "components/PageContainer";
import { Toolbar } from "components/Toolbar";
import { Box, Typography, LinearProgress } from "@mui/material";
import { CustomAccordion } from "components/accordion";
import { dashboardService } from "services/dashboardService";
import { ComplaintsTable } from "./components/Table";
import { GroupSummary } from "./components/GroupSummary";
import { MainContainer } from "./styles"
import { toast } from "react-toastify";
import PageLoader from "components/Table/PageLoader";
import { NoDataMessage } from "components/NoDataMessage/styles";
import { DashboardPaginationProps } from "interfaces/IPagination";
import { CustomPagination } from "components/CustomPagination";
import { dateToday } from "utils/dateToday";
import { useSearchParams } from "react-router-dom";
import { IComplaintsResponse, IComplaint, IComplaintsParams } from "interfaces/IComplaints";
import { ActiveFilters } from "components/ActiveFilters";
import dayjs from "dayjs";

type SortField = 'created' | 'reason';

interface AccordionDetail {
  [groupName: string]: {
    data: DashboardPaginationProps<IComplaint>;
    loading: boolean;
    isFirstLoad: boolean;
    currentPage: number;
    groupId: number;
    orderBy?: SortField;
    orderDirection?: 'asc' | 'desc';
  };
}

export const Complaints = () => {
  const [complaintsData, setComplaintsData] = useState<DashboardPaginationProps<IComplaintsResponse>>({
    page: 1,
    perPage: 10,
    totalItems: 0,
    totalPages: 1,
    items: []
  });
  const [loading, setLoading] = useState(false);
  const [accordionDetails, setAccordionDetails] = useState<AccordionDetail>({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [params, setParams] = useState<IComplaintsParams>(() => {
    const startDate = searchParams.get("startDate") || dateToday();
    const endDate = searchParams.get("endDate") || dateToday();
    const page = Number(searchParams.get("page")) || 1;
    const order = searchParams.get("order") || "desc";
    const search = searchParams.get("search") || "";
    const perPage = Number(searchParams.get("perPage")) || 10;
    const cds = searchParams.getAll("cds") || [];
    const transshipPointsIdsFromUrl = searchParams.getAll("transshipPointsIds");
    const reasons = searchParams.getAll("reasons") || [];
    const groupBy = searchParams.get("groupBy") || "driver";

    return {
      startDate,
      endDate,
      page,
      perPage,
      order,
      search,
      cds,
      transshipPointsIds: transshipPointsIdsFromUrl.map((id: string) => parseInt(id)).filter((id: number) => !isNaN(id)) || [],
      reasons,
      groupBy
    }
  });

  const onChangeParams = (newParamsOrFn: Function | typeof params) => {
    const updatedParams = typeof newParamsOrFn === 'function' ? newParamsOrFn(params) : newParamsOrFn;
    setParams(updatedParams);

    const currentParams = Object.fromEntries(searchParams.entries());
    const combinedParams = { ...currentParams, ...updatedParams };
    setSearchParams(combinedParams as any);
  };

  const filterMapper = {
    startDate: {
      label: "Data início",
      formatValue: (value: string) => {
        try {
          return dayjs(value).format('DD/MM/YYYY');
        } catch {
          return value;
        }
      },
    },
    endDate: {
      label: "Data fim",
      formatValue: (value: string) => {
        try {
          return dayjs(value).format('DD/MM/YYYY');
        } catch {
          return value;
        }
      },
      hidden: params.startDate === params.endDate, // Oculta se for período de um dia
    },
    transshipPointsIds: {
      label: "Centros de Distribuição",
      formatValue: (value: number[]) => `${value.length} selecionado${value.length !== 1 ? 's' : ''}`,
      hidden: !params.transshipPointsIds || params.transshipPointsIds.length === 0,
    },
    reasons: {
      label: "Motivos",
      formatValue: (value: string[]) => `${value.length} selecionado${value.length !== 1 ? 's' : ''}`,
      hidden: !params.reasons || params.reasons.length === 0,
    },
    groupBy: {
      label: "Agrupamento",
      formatValue: (value: string) => value === 'driver' ? 'Motorista' : 'Time',
      hidden: params.groupBy === 'driver',
    },
    order: {
      label: "Ordenação",
      formatValue: (value: string) => value === 'desc' ? 'Maior quebra (%)' : 'Menor quebra (%)',
      hidden: params.order === 'desc',
    },
  };

  // Remove um filtro específico
  const handleRemoveFilter = (filterKey: string) => {
    const newParams = { ...params };

    switch (filterKey) {
      case 'startDate':
      case 'endDate':
        const todayFormatted = dateToday();
        newParams.startDate = todayFormatted;
        newParams.endDate = todayFormatted;
        break;
      case 'transshipPointsIds':
        newParams.transshipPointsIds = [];
        break;
      case 'reasons':
        newParams.reasons = [];
        break;
      case 'groupBy':
        newParams.groupBy = 'driver';
        break;
      case 'order':
        newParams.order = 'desc';
        break;
      case 'search':
        newParams.search = '';
        break;
      default:
        if (filterKey in newParams) {
          (newParams as any)[filterKey] = undefined;
        }
    }

    newParams.page = 1;
    onChangeParams(newParams);
  };

  // Remove todos os filtros
  const handleClearAllFilters = () => {
    const clearedParams = {
      startDate: dateToday(),
      endDate: dateToday(),
      transshipPointsIds: [],
      reasons: [],
      groupBy: 'driver',
      order: 'desc',
      search: '',
      page: 1,
    };
    onChangeParams(clearedParams);
  };

  const fetchComplaintsData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dashboardService.getComplaintsData(params);
      setComplaintsData(response);
    } catch (error) {
      toast.error('Erro ao buscar dados de reclamações');
    } finally {
      setLoading(false);
    }
  }, [params]);

  const fetchGroupDetails = useCallback(async (
    groupName: string,
    groupId: number,
    page: number = 1,
    orderBy?: SortField,
    orderDirection?: 'asc' | 'desc'
  ) => {
    const currentDetail = accordionDetails[groupName];
    const isFirstLoad = !currentDetail;

    // Atualiza o estado para mostrar loading
    setAccordionDetails(prev => ({
      ...prev,
      [groupName]: {
        ...prev[groupName],
        loading: true,
        isFirstLoad,
        currentPage: page,
        groupId,
        orderBy,
        orderDirection,
      }
    }));

    try {
      const detailParams = {
        ...params,
        groupId, // Usa o groupId para filtrar
        page,
        orderBy,
        orderDirection,
      };

      const response = await dashboardService.getComplaintsDetail(detailParams);

      setAccordionDetails(prev => ({
        ...prev,
        [groupName]: {
          ...prev[groupName],
          data: response,
          loading: false,
          isFirstLoad: false,
          currentPage: page,
          groupId,
          orderBy,
          orderDirection,
        }
      }));
    } catch (error) {
      toast.error('Erro ao buscar detalhes das reclamações');
      setAccordionDetails(prev => ({
        ...prev,
        [groupName]: {
          ...prev[groupName],
          loading: false,
          isFirstLoad: false,
        }
      }));
    }
  }, [params, accordionDetails]);

  useEffect(() => {
    fetchComplaintsData();
    // Limpa os detalhes dos acordeões quando os parâmetros principais mudam
    setAccordionDetails({});
  }, [fetchComplaintsData]);

  const handleTableSort = (groupName: string, orderBy: SortField, orderDirection: 'asc' | 'desc') => {
    // Sempre volta para a página 1 quando ordena
    const groupDetail = accordionDetails[groupName];
    if (groupDetail?.groupId) {
      fetchGroupDetails(groupName, groupDetail.groupId, 1, orderBy, orderDirection);
    }
  };

  return (
    <div className="pageContent">
      <PageContainer>
        <MainContainer>
          <div className='infos'>
            <h3>Quebras</h3>
            <Toolbar
              params={params}
              paramState={onChangeParams}
              loadingState={loading}
              placeholder={params.groupBy === 'driver' ? 'Motorista' : 'Time'}
              page="complaints"
            />
            <div className='filters'>
              <ActiveFilters
                params={params}
                mapper={filterMapper}
                onRemoveFilter={handleRemoveFilter}
                onClearAll={handleClearAllFilters}
                title="Filtros aplicados"
                hideWhenEmpty={true}
              />
            </div>
          </div>
          {loading ? (
            <PageLoader height='35vh' />
          ) : (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              {complaintsData?.items?.length > 0 ? (
                <>
                  <CustomAccordion
                    data={complaintsData.items}
                    renderSummary={(group) => (
                      <Box display="flex" alignItems="center" width="100%" justifyContent={"space-between"}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.1rem' },
                            fontWeight: 600,
                            color: 'rgba(0, 0, 0, 0.87)'
                          }}
                        >
                          {group.driverName || group.groupName}
                        </Typography>
                        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                          <GroupSummary group={group} />
                        </Box>
                      </Box>
                    )}
                    onChange={(groupName: string, isExpanded: boolean) => {
                      if (isExpanded && !accordionDetails[groupName]) {
                        const group = complaintsData.items.find(item => item.groupName === groupName);
                        if (group) {
                          fetchGroupDetails(groupName, group.id);
                        }
                      }
                    }}
                  >
                    {(group) => {
                      const groupDetail = accordionDetails[group.groupName];

                      return (
                        <>
                          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                            <GroupSummary group={group} isMobile />
                          </Box>

                          {groupDetail?.loading && groupDetail?.isFirstLoad ? (
                            <Box sx={{ width: '100%', mt: 2 }}>
                              <LinearProgress />
                              <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                                Carregando detalhes...
                              </Typography>
                            </Box>
                          ) : groupDetail?.data?.items ? (
                            <>
                              <ComplaintsTable
                                complaints={groupDetail.data.items}
                                currentOrderBy={groupDetail.orderBy}
                                currentOrderDirection={groupDetail.orderDirection}
                                onSort={(orderBy, orderDirection) => handleTableSort(group.groupName, orderBy, orderDirection)}
                                showLoading={groupDetail?.loading && !groupDetail?.isFirstLoad}
                              />

                              {/* Paginação para os detalhes */}
                              {groupDetail.data.totalPages > 1 && (
                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                                  <CustomPagination
                                    currentPage={groupDetail.currentPage}
                                    totalPages={groupDetail.data.totalPages}
                                    onPageChange={(page) => fetchGroupDetails(group.groupName, group.id, page, groupDetail.orderBy, groupDetail.orderDirection)}
                                  />
                                </Box>
                              )}
                            </>
                          ) : (
                            <Typography variant="body2" sx={{ textAlign: 'center', py: 2 }}>
                              Expanda para ver os detalhes
                            </Typography>
                          )}
                        </>
                      );
                    }}
                  </CustomAccordion>

                  {/* Paginação principal para os grupos */}
                  {complaintsData.totalPages > 1 && (
                    <CustomPagination
                      currentPage={params.page || 1}
                      totalPages={complaintsData.totalPages}
                      onPageChange={(value) => onChangeParams({ ...params, page: value })}
                    />
                  )}
                </>
              ) : (
                <NoDataMessage ht="35vh">
                  <h3>Não há dados para exibir :(</h3>
                  <p>Verifique os filtros aplicados e tente novamente.</p>
                </NoDataMessage>
              )}
            </Box>
          )}
        </MainContainer>
      </PageContainer>
    </div>
  );
};
